import React from "react";
import {
  Box,
  Button,
  IconButton,
  Modal,
  Typography,
  useTheme,
  Chip,
  Grid,
  Paper
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import useMediaQuery from "@mui/material/useMediaQuery";
import { LoadingButton } from "@mui/lab";
import { useSelector } from "react-redux";

import { Customer } from "./types/customer";
import { CustomerType } from "./enums/customerType";
import {
  BRAND_COLOR,
  BRAND_HOVER,
  useKivunovaTranslation
} from "@kivunova/kivufrontendcommon";
import { useDeleteCustomer } from "../../hooks/api/useCustomer";
import { RootState } from "../../store/store";

interface ViewCustomerModalProps {
  open: boolean;
  onClose: () => void;
  customer: Customer;
  onDelete?: () => void;
}

const ViewCustomerModal: React.FC<ViewCustomerModalProps> = ({
  open,
  onClose,
  customer,
  onDelete
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.down("md"));
  const deleteCustomer = useDeleteCustomer();
  const { t } = useKivunovaTranslation();

  /* ------------------------------------------------------------------
   * Resolve companyId from all possible sources
   * ------------------------------------------------------------------ */
  const userCompanyId = useSelector(
    (state: RootState) => state.user.userDetails?.companyId
  );
  const profileCompanyId = useSelector(
    (state: RootState) => state.profile.companyData?.id
  );
  const employeeCompanyId = useSelector(
    (state: RootState) => state.employees.employeeDetail?.companyId
  );
  const storeCompanyId = useSelector(
    (state: RootState) => state.store.storeDetails?.companyId
  );

  const resolvedCompanyId =
    profileCompanyId ?? userCompanyId ?? employeeCompanyId ?? storeCompanyId;

  /* ------------------------------------------------------------------
   * Delete handler
   * ------------------------------------------------------------------ */
  const handleDelete = async () => {
    if (!resolvedCompanyId) {
      console.error("❌ Cannot delete customer: companyId not found");
      return;
    }

    try {
      await deleteCustomer.mutateAsync({
        customerId: customer.id,
        companyId: resolvedCompanyId
      });

      onDelete?.();
      onClose();
    } catch (err) {
      console.error("❌ Failed to delete customer:", err);
    }
  };

  /* ------------------------------------------------------------------
   * Build fields for display
   * ------------------------------------------------------------------ */
  const field = (
    label: string,
    value?: string | React.ReactNode
  ): [string, string | React.ReactNode] | null => {
    if (value === undefined || value === null || value === "") {
      return null;
    }
    return [label, value];
  };

  const fields = [
    field(t("customer-field-name", "Name"), customer.name),
    field(
      t("customer-field-type", "Customer Type"),
      <Chip
        key="type"
        label={customer.type}
        size="small"
        color={customer.type === CustomerType.COMPANY ? "primary" : "default"}
      />
    ),
    field(
      t("customer-field-tin", "TIN (Tax Identification Number)"),
      customer.tin
    ),
    field(t("customer-field-phone", "Phone"), customer.phone),
    field(t("customer-field-email", "Email"), customer.email),
    field(t("customer-field-country", "Country"), customer.address?.country),
    field(t("customer-field-province", "Province"), customer.address?.province),
    field(t("customer-field-district", "District"), customer.address?.district),
    field(t("customer-field-sector", "Sector"), customer.address?.sector),
    field(t("customer-field-cell", "Cell"), customer.address?.cell),
    field(t("customer-field-village", "Village"), customer.address?.village),
    field(
      t("customer-field-street", "Street Address"),
      customer.address?.streetAddress
    )
  ].filter(Boolean) as [string, string | React.ReactNode][];

  return (
    <Modal open={open} onClose={onClose}>
      <Box
        className="modal-styles"
        sx={{
          padding: isMobile ? theme.spacing(2) : theme.spacing(3),
          maxWidth: isTablet ? "90vw" : "600px",
          margin: "auto"
        }}
      >
        {/* Header */}
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mb={3}
        >
          <Typography
            variant={isMobile ? "h6" : "h5"}
            fontWeight="bold"
            sx={{ color: theme.palette.text.primary }}
          >
            {t("customer-modal-title", "Customer Details")}
          </Typography>
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>

        {/* Details Card */}
        <Paper
          elevation={1}
          sx={{
            p: isMobile ? 1 : 2,
            borderRadius: 2,
            mb: 3,
            backgroundColor: theme.palette.background.paper
          }}
        >
          <Grid container>
            {fields.map(([label, value], index) => (
              <React.Fragment key={label}>
                <Grid
                  sx={{
                    width: "100%",
                    display: "flex",
                    flexDirection: isMobile ? "column" : "row",
                    borderBottom:
                      index === fields.length - 1
                        ? "none"
                        : `1px solid ${theme.palette.divider}`
                  }}
                >
                  <Box
                    sx={{
                      flex: 1,
                      p: isMobile ? 1 : 1.5,
                      borderRight: isMobile
                        ? "none"
                        : `1px solid ${theme.palette.divider}`,
                      borderBottom: isMobile
                        ? `1px solid ${theme.palette.divider}`
                        : "none"
                    }}
                  >
                    <Typography
                      variant="body2"
                      fontWeight={600}
                      color="text.secondary"
                    >
                      {label}:
                    </Typography>
                  </Box>
                  <Box sx={{ flex: 2, p: isMobile ? 1 : 1.5 }}>
                    {typeof value === "string" ? (
                      <Typography variant="body2">{value || "-"}</Typography>
                    ) : (
                      value
                    )}
                  </Box>
                </Grid>
              </React.Fragment>
            ))}
          </Grid>
        </Paper>

        {/* Actions */}
        <Box
          display="flex"
          gap={2}
          justifyContent="flex-end"
          flexDirection={isMobile ? "column" : "row"}
        >
          <Button
            variant="outlined"
            onClick={onClose}
            fullWidth={isMobile}
            sx={{
              borderColor: BRAND_COLOR,
              color: BRAND_COLOR,
              "&:hover": {
                backgroundColor: BRAND_HOVER,
                color: "white"
              },
              textTransform: "none"
            }}
          >
            {t("close", "Close")}
          </Button>

          <LoadingButton
            variant="contained"
            color="error"
            startIcon={<DeleteOutlineOutlinedIcon />}
            onClick={handleDelete}
            loading={deleteCustomer.isPending}
            fullWidth={isMobile}
            sx={{ textTransform: "none" }}
          >
            {t("delete", "Delete")}
          </LoadingButton>
        </Box>
      </Box>
    </Modal>
  );
};

export default ViewCustomerModal;
